import { API_CONFIG, getAuthHeaders } from '../config/api';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export interface ChatResponse {
  success: boolean;
  response: string;
  timestamp: string;
  model?: string;
  fallback?: boolean;
  error?: string;
}

export interface SendMessageRequest {
  message: string;
  context?: string;
  conversationHistory?: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
}

class ChatService {
  private baseUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT}`;

  /**
   * Send a message to the AI chatbot
   */
  async sendMessage(request: SendMessageRequest): Promise<ChatResponse> {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Authentication required. Please log in to use the chatbot.');
      }

      const response = await fetch(`${this.baseUrl}/message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication expired. Please log in again.');
        }
        if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to use the chatbot.');
        }

        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: ChatResponse = await response.json();
      return data;

    } catch (error: any) {
      console.error('Chat service error:', error);
      throw new Error(error.message || 'Failed to send message');
    }
  }

  /**
   * Convert chat messages to conversation history format
   */
  convertToConversationHistory(messages: ChatMessage[]): Array<{ role: 'user' | 'assistant', content: string }> {
    return messages
      .filter(msg => msg.sender !== 'bot' || msg.text !== 'Hello! I\'m your AI assistant for due diligence processes. I can help you navigate the platform, analyze documents, and answer questions about your deals. How can I assist you today?') // Skip initial greeting
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
        content: msg.text
      }));
  }

  /**
   * Get context based on current page/section
   */
  getContextFromLocation(pathname: string): string {
    if (pathname.includes('/deals')) {
      return 'User is in the Deals section - deal management and overview';
    }
    if (pathname.includes('/document-upload')) {
      return 'User is in the Document Upload section - uploading and organizing documents';
    }
    if (pathname.includes('/ai-review')) {
      return 'User is in the AI Review section - AI analysis and insights';
    }
    if (pathname.includes('/report-generation')) {
      return 'User is in the Report Generation section - creating due diligence reports';
    }
    if (pathname.includes('/settings')) {
      return 'User is in the Settings section - configuring platform preferences';
    }
    if (pathname.includes('/dashboard')) {
      return 'User is on the Dashboard - overview and navigation';
    }
    return 'General platform inquiry';
  }

  /**
   * Generate a unique message ID
   */
  generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a chat message object
   */
  createMessage(text: string, sender: 'user' | 'bot'): ChatMessage {
    return {
      id: this.generateMessageId(),
      text,
      sender,
      timestamp: new Date()
    };
  }

  /**
   * Get initial greeting message
   */
  getInitialMessage(): ChatMessage {
    return this.createMessage(
      "Hello! I'm your AI assistant for due diligence processes. I can help you navigate the platform, analyze documents, and answer questions about your deals. How can I assist you today?",
      'bot'
    );
  }

  /**
   * Validate message before sending
   */
  validateMessage(message: string): { isValid: boolean; error?: string } {
    if (!message || !message.trim()) {
      return { isValid: false, error: 'Message cannot be empty' };
    }

    if (message.length > 2000) {
      return { isValid: false, error: 'Message is too long (max 2000 characters)' };
    }

    return { isValid: true };
  }

  /**
   * Format error message for display
   */
  formatErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    return 'An unexpected error occurred. Please try again.';
  }
}

export const chatService = new ChatService();
export default chatService;
